/**
 * L'assegnazione dei posti: quanti hanno chiesto e quanti sono assegnati per
 * ogni sessione, e la proposta automatica "in ordine di arrivo finche' ci
 * sono posti". La proposta e' solo un punto di partenza: chi organizza la
 * ritocca e poi conferma.
 */

import { sessionePerCodice, sessioniDelPercorso } from './regole.ts';
import type { PartecipantePerAssegnazione, Percorso, ProgrammaEvento } from './tipi.ts';

export interface ConteggioSessione {
  richiesti: number;
  /** Assegnati in bozza + confermati. */
  assegnati: number;
  confermati: number;
  /** Posti dell'aula; null = nessun limite. */
  posti: number | null;
}

/** Richiesti e assegnati per ogni sessione sceglibile del programma. */
export function conteggiPerSessione(
  programma: ProgrammaEvento,
  partecipanti: PartecipantePerAssegnazione[],
): Map<string, ConteggioSessione> {
  const posti = new Map(programma.aule.map((a) => [a.codice, a.posti]));
  const conteggi = new Map<string, ConteggioSessione>(
    programma.sessioni
      .filter((s) => s.sceglibile)
      .map((s) => [s.codice, { richiesti: 0, assegnati: 0, confermati: 0, posti: posti.get(s.aula) ?? null }]),
  );
  for (const p of partecipanti) {
    for (const c of sessioniDelPercorso(p.richiesta)) {
      const riga = conteggi.get(c);
      if (riga) riga.richiesti += 1;
    }
    if (p.assegnazione_stato === 'nessuna') continue;
    for (const c of sessioniDelPercorso(p.assegnazione)) {
      const riga = conteggi.get(c);
      if (!riga) continue;
      riga.assegnati += 1;
      if (p.assegnazione_stato === 'confermata') riga.confermati += 1;
    }
  }
  return conteggi;
}

export interface SenzaPosto {
  partecipanteId: string;
  giorno: string;
  turno: number;
  /** La sessione chiesta che era piena. */
  sessione: string;
}

export interface Proposta {
  /** Le nuove bozze, per partecipante. Chi e' gia' confermato non compare. */
  bozze: Map<string, Percorso>;
  /** I turni rimasti vuoti perche' la sessione chiesta era piena: da sistemare a mano. */
  senzaPosto: SenzaPosto[];
}

/**
 * Propone l'assegnazione a chi ha inviato la richiesta e non e' ancora
 * confermato. Prima si contano i posti gia' presi dai confermati (non si
 * toccano); poi, in ordine di arrivo, ognuno riceve quello che ha chiesto
 * finche' l'aula ha posti. Se una sessione e' piena il turno resta vuoto
 * (una sessione lunga resta vuota in tutti i suoi turni).
 */
export function proponiAssegnazione(
  programma: ProgrammaEvento,
  partecipanti: PartecipantePerAssegnazione[],
): Proposta {
  const perCodice = sessionePerCodice(programma);
  const posti = new Map(programma.aule.map((a) => [a.codice, a.posti]));
  const occupati = new Map<string, number>();
  const occupa = (codice: string) => occupati.set(codice, (occupati.get(codice) ?? 0) + 1);

  for (const p of partecipanti) {
    if (p.assegnazione_stato === 'confermata') for (const c of sessioniDelPercorso(p.assegnazione)) occupa(c);
  }

  const daAssegnare = partecipanti
    .filter((p) => p.assegnazione_stato !== 'confermata' && p.richiesta && p.richiesta_at)
    .sort((a, b) => (a.richiesta_at ?? '').localeCompare(b.richiesta_at ?? '') || a.id.localeCompare(b.id));

  const bozze = new Map<string, Percorso>();
  const senzaPosto: SenzaPosto[] = [];

  for (const p of daAssegnare) {
    const assegnato: Percorso = {};
    const decise = new Map<string, boolean>();
    for (const [giorno, delGiorno] of Object.entries(p.richiesta ?? {})) {
      for (const [turno, codice] of Object.entries(delGiorno)) {
        const s = perCodice.get(codice);
        if (!s) continue;
        let entra = decise.get(codice);
        if (entra === undefined) {
          const limite = posti.get(s.aula) ?? null;
          entra = limite === null || (occupati.get(codice) ?? 0) < limite;
          if (entra) occupa(codice);
          decise.set(codice, entra);
        }
        if (entra) {
          assegnato[giorno] = { ...(assegnato[giorno] ?? {}), [turno]: codice };
        } else {
          senzaPosto.push({ partecipanteId: p.id, giorno, turno: Number(turno), sessione: codice });
        }
      }
    }
    bozze.set(p.id, assegnato);
  }
  return { bozze, senzaPosto };
}

/** Vero se il percorso ha una sessione valida in ogni turno di ogni giorno. */
export function assegnazioneCompleta(programma: ProgrammaEvento, percorso: Percorso | null | undefined): boolean {
  if (!percorso) return false;
  const perCodice = sessionePerCodice(programma);
  return programma.giorni.every((g) =>
    programma.turni.every((t) => {
      const s = perCodice.get(percorso[g.codice]?.[String(t.n)] ?? '');
      return Boolean(s && s.giorno === g.codice && s.turni.includes(t.n));
    }),
  );
}
